import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Mail, Link2, DollarSign, Eye } from "lucide-react";
import { statusBadge, statusLabel } from "@/lib/invoice-status";
import type { ReceivableRow } from "./ReceivablesTable";

type UnpaidInvoice = {
  id: number;
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  total: string;
  paidAmount: string;
  outstanding: number;
  status: string;
  issuedAt: string | null;
  sentAt: string | null;
  publicToken: string | null;
};

type Props = {
  recipient: ReceivableRow | null;
  open: boolean;
  onClose: () => void;
  onSendPayLink: (invoice: UnpaidInvoice) => void;
  onRecordPayment: (invoice: UnpaidInvoice) => void;
  onViewInvoice?: (invoice: UnpaidInvoice) => void;
  onEmailInvoice?: (invoice: UnpaidInvoice) => void;
};

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export default function ReceivablesDrillDown({ recipient, open, onClose, onSendPayLink, onRecordPayment, onViewInvoice, onEmailInvoice }: Props) {
  const { data, isLoading } = useQuery<UnpaidInvoice[]>({
    queryKey: ['/api/admin/owner-invoices/receivables', recipient?.recipientType, recipient?.recipientId],
    enabled: open && !!recipient,
    queryFn: async () => {
      const res = await fetch(`/api/admin/owner-invoices/receivables/${recipient!.recipientType}/${recipient!.recipientId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      return res.json();
    },
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Unpaid invoices — {recipient?.recipientName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2 py-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (data?.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm py-4">No unpaid invoices.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).map(inv => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}
                    </TableCell>
                    <TableCell className="text-right text-sm">${Number(inv.total).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-semibold text-sm text-red-600">${inv.outstanding.toFixed(2)}</TableCell>
                    <TableCell>
                      {(() => {
                        const badge = statusBadge(inv.status);
                        return (
                          <Badge variant={badge.variant} className={`text-xs ${badge.className}`}>
                            {statusLabel(inv.status)}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmtDate(inv.issuedAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {onViewInvoice && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title="View invoice"
                            onClick={() => onViewInvoice(inv)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {onEmailInvoice && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title="Send email"
                            onClick={() => onEmailInvoice(inv)}
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {inv.publicToken && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title="Send pay link"
                            onClick={() => onSendPayLink(inv)}
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          title="Record payment"
                          onClick={() => onRecordPayment(inv)}
                        >
                          <DollarSign className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
